import { Link, createFileRoute, notFound } from "@tanstack/react-router";

import {
  CihArticleRows,
  type ArticleRowData,
} from "~/components/cih/article-rows";
import {
  CihAtAGlanceCard,
  type AtAGlanceRow,
} from "~/components/cih/at-a-glance-card";
import { CihCcoTrustStrip } from "~/components/cih/cco-trust-strip";
import { CihChallengeHero } from "~/components/cih/challenge-hero";
import { CihClosingCta } from "~/components/cih/closing-cta";
import type { CihCoachCellData } from "~/components/cih/coach-cell";
import { CihCoachGrid } from "~/components/cih/coach-grid";
import { CihFaqSection } from "~/components/cih/faq-section";
import { CihFrameworkCards } from "~/components/cih/framework-cards";
import {
  CihProgramRows,
  type ProgramRowData,
} from "~/components/cih/program-rows";
import { RelatedEntityPills } from "~/components/cih/related-entity-pills";
import { CihSection } from "~/components/cih/section";
import { CihSectionHeader } from "~/components/cih/section-header";
import { CihStatStrip } from "~/components/cih/stat-strip";
import { CihTabNav, type CihTab } from "~/components/cih/tab-nav";
import { TrustSignalBadge } from "~/components/cih/trust-signal-badge";
import { Button } from "~/components/ui/button";
import { CDNImage } from "~/components/ui/cdn-image";
import { RichTextViewer } from "~/components/ui/rich-text-editor";
import {
  getChallengeArticlesFn,
  type ChallengeArticleRow,
} from "~/lib/functions/cih/articles";
import {
  getCcosByChallengeFn,
  getCoachesByChallengeFn,
  getCtpsByChallengeFn,
  getPublishedChallengeWithRelationsFn,
  type ChallengeCcoRow,
  type ChallengeCtpRow,
  type ChallengeWithRelations,
} from "~/lib/functions/cih/challenges";
import { dropRestatedOpeningHeading } from "~/lib/shared/cih/body-toc";
import {
  createBrandMeta,
  createBreadcrumbStructuredData,
  createFaqStructuredData,
} from "~/lib/shared/meta";

type CoachesResult = Awaited<ReturnType<typeof getCoachesByChallengeFn>>;
type CoachRow = CoachesResult["coaches"][number];

type LoaderData = {
  title: string;
  challenge: ChallengeWithRelations;
  coaches: CoachRow[];
  coachesTotal: number;
  articles: ChallengeArticleRow[];
  ctps: ChallengeCtpRow[];
  ccos: ChallengeCcoRow[];
};

export const Route = createFileRoute("/_public/coaching-for/$challengeSlug/")({
  staticData: { title: "Challenge" },
  loader: async ({ params }): Promise<LoaderData> => {
    const challenge = await getPublishedChallengeWithRelationsFn({
      data: { slug: params.challengeSlug },
    });
    if (!challenge) throw notFound();
    const [coachesResult, articles, ctps, ccos] = await Promise.all([
      getCoachesByChallengeFn({
        data: { challengeId: challenge.id, page: 1, limit: 6 },
      }),
      getChallengeArticlesFn({ data: { challengeId: challenge.id } }),
      getCtpsByChallengeFn({ data: { challengeId: challenge.id } }),
      getCcosByChallengeFn({ data: { challengeId: challenge.id } }),
    ]);
    return {
      title: challenge.title,
      challenge,
      coaches: coachesResult.coaches,
      coachesTotal: coachesResult.total,
      articles,
      ctps,
      ccos,
    };
  },
  head: ({
    loaderData,
    params,
  }: {
    loaderData?: LoaderData;
    params: { challengeSlug: string };
  }) => {
    const challenge = loaderData?.challenge;
    const url = `https://dreamcoachmatch.com/coaching-for/${params.challengeSlug}`;
    const base = createBrandMeta({
      title: challenge ? `${challenge.title} | Coaching For` : "Coaching For",
      description:
        challenge?.description ||
        "Discover coaching resources for this challenge.",
      ogUrl: url,
      ogImage: challenge?.heroImageUrl ?? undefined,
      canonical: url,
    });
    if (!challenge) return base;

    const breadcrumbLd = createBreadcrumbStructuredData([
      { name: "Home", url: "https://dreamcoachmatch.com" },
      { name: "Guidance", url: "https://dreamcoachmatch.com/coaching-for" },
      { name: challenge.title, url },
    ]);
    const faqLd =
      (challenge.faqs?.length ?? 0) > 0
        ? createFaqStructuredData(challenge.faqs ?? [])
        : null;

    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
        ...(faqLd
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(faqLd),
              },
            ]
          : []),
      ],
    };
  },
  component: ChallengeDetailPage,
});

function ChallengeDetailPage() {
  const { challenge, coaches, coachesTotal, articles, ctps, ccos } =
    Route.useLoaderData();
  const { challengeSlug } = Route.useParams();

  const programRows: ProgramRowData[] = ctps.map((c) => ({
    id: c.id,
    slug: c.slug,
    name: c.name,
    logoUrl: c.logoUrl,
    deliveryFormat: c.deliveryFormat,
    accreditationLevel: c.accreditationLevel,
    duration: c.duration,
    tuitionRange: c.tuitionRange,
  }));

  const articleRows: ArticleRowData[] = articles.map((a) => ({
    id: a.id,
    slug: a.slug,
    title: a.title,
    description: a.description,
    authorName: a.coachFullName ?? a.authorName ?? "",
  }));

  const intakeSearch = {
    source: `challenge:${challengeSlug}`,
    ...(challenge.intakeTrigger ? { trigger: challenge.intakeTrigger } : {}),
  };

  const metaRows: AtAGlanceRow[] = [
    { key: "Reviewer", value: challenge.reviewerName },
    { key: "Credentials", value: challenge.reviewerCredentials },
    { key: "Reviewed", value: challenge.reviewedDate },
  ];

  const statCells = [
    {
      value: coachesTotal > 0 ? String(coachesTotal) : "0",
      label: "Coaches",
    },
    {
      value:
        challenge.frameworks.length > 0
          ? String(challenge.frameworks.length)
          : "0",
      label: "Approaches",
    },
    {
      value: articles.length > 0 ? String(articles.length) : "0",
      label: "Articles",
    },
  ];

  const faqs = challenge.faqs ?? [];

  const relatedPills = challenge.relatedChallenges
    .filter(
      (
        c,
      ): c is (typeof challenge.relatedChallenges)[number] & { slug: string } =>
        c.slug != null,
    )
    .map((c) => ({ label: c.title, href: `/coaching-for/${c.slug}` }));

  const coachCells: CihCoachCellData[] = coaches.map((c) => ({
    id: c.id,
    username: c.username,
    fullName: c.fullName,
    profilePictureUrl: c.profilePictureUrl,
    credential: c.credential,
    description: c.description,
  }));

  const tabs: CihTab[] = [
    { id: "overview", label: "Overview" },
    {
      id: "approaches",
      label: "Approaches",
      count: challenge.frameworks.length,
    },
    { id: "programs", label: "Programs", count: programRows.length },
    { id: "coaches", label: "Coaches", count: coachesTotal },
    { id: "articles", label: "Articles", count: articles.length },
    { id: "related", label: "Related", count: relatedPills.length },
    { id: "faq", label: "FAQ", count: faqs.length },
  ];

  return (
    <>
      <CihChallengeHero
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Guidance", href: "/coaching-for" },
          {
            label: challenge.title,
            href: `/coaching-for/${challengeSlug}`,
          },
        ]}
        kicker="Coaching for"
        title={challenge.title}
        lede={challenge.subtitle ?? challenge.tagline ?? challenge.description}
        cta={
          <Link to="/intake" search={intakeSearch}>
            <Button variant="primary" size="lg">
              Find the right support →
            </Button>
          </Link>
        }
        meta={<CihAtAGlanceCard label="Reviewed by" rows={metaRows} />}
        trustBadge={
          <TrustSignalBadge
            reviewerName={challenge.reviewerName}
            reviewerCredentials={challenge.reviewerCredentials}
            reviewDate={challenge.reviewedDate}
            variant="dark"
          />
        }
      />

      <CihStatStrip cells={statCells} />
      <CihTabNav tabs={tabs} />

      <CihSection id="overview" divider={false}>
        <CihSectionHeader label="Overview" title="About" />
        <div className="mx-auto max-w-7xl px-4 xl:px-0">
          {challenge.heroImageUrl ? (
            <div className="mb-10 overflow-hidden rounded-2xl">
              <CDNImage
                src={challenge.heroImageUrl}
                alt={challenge.title}
                className="aspect-video w-full object-cover"
                width={1200}
                loading="eager"
              />
            </div>
          ) : null}
          {challenge.body ? (
            <div className="prose prose-lg max-w-none">
              <RichTextViewer
                content={JSON.stringify(
                  dropRestatedOpeningHeading(challenge.body),
                )}
              />
            </div>
          ) : null}
        </div>
      </CihSection>

      {coachCells.length > 0 ? (
        <CihSection id="coaches">
          <CihCoachGrid
            coaches={coachCells}
            totalCount={coachesTotal}
            sectionLabel="Coaches"
            heading="Coaches who can help"
            seeAllHref={`/coaching-for/${challengeSlug}/coaches`}
          />
        </CihSection>
      ) : null}

      {challenge.frameworks.length > 0 ? (
        <CihSection id="approaches">
          <CihFrameworkCards
            frameworks={challenge.frameworks}
            sectionLabel="Approaches"
            heading="Proven approaches coaches use"
          />
        </CihSection>
      ) : null}

      {ccos.length > 0 ? (
        <CihSection>
          <CihCcoTrustStrip
            items={ccos}
            sectionLabel="Trusted standards"
            heading="Credentialed by"
          />
        </CihSection>
      ) : null}

      {programRows.length > 0 ? (
        <CihSection id="programs">
          <CihProgramRows
            programs={programRows}
            sectionLabel="Training programs"
            heading="Programs that train coaches in these approaches"
          />
        </CihSection>
      ) : null}

      {articleRows.length > 0 ? (
        <CihSection id="articles">
          <CihArticleRows
            articles={articleRows}
            sectionLabel="Articles"
            heading={`Reading from coaches on ${challenge.title}`}
          />
        </CihSection>
      ) : null}

      {relatedPills.length > 0 ? (
        <CihSection id="related">
          <RelatedEntityPills
            items={relatedPills}
            sectionLabel="Explore more"
            heading="Related challenges"
          />
        </CihSection>
      ) : null}

      {faqs.length > 0 ? (
        <CihSection id="faq">
          <CihFaqSection
            faqs={faqs}
            heading={`Questions about ${challenge.title}`}
          />
        </CihSection>
      ) : null}

      <CihClosingCta
        headline="Your next chapter starts with a question."
        sub="Free · 5 minutes · No commitment"
        cta={
          <Link to="/intake" search={intakeSearch}>
            <Button variant="primary" size="lg">
              Start the conversation →
            </Button>
          </Link>
        }
      />
    </>
  );
}
